import {CommonModule} from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  OnDestroy,
  signal,
  viewChild
} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {CameraScannerSession, QrCameraScannerService} from '../../shared/services/qr-camera-scanner.service';
import {TRADE_BASE} from '../trade/trade.constants';

const DEFAULT_RETURN_TO = TRADE_BASE;
const DEFAULT_TARGET = `${TRADE_BASE}/receive`;

@Component({
  selector: 'app-mobile-qr-scan',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './mobile-qr-scan.html',
  styleUrl: './mobile-qr-scan.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MobileQrScan implements AfterViewInit, OnDestroy {
  private static readonly NAVIGATION_DELAY_MS = 1200;

  private readonly scanner = inject(QrCameraScannerService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  protected readonly scannerError = signal('');
  protected readonly scanning = signal(false);
  protected readonly navigating = signal(false);
  protected readonly detectedCode = signal('');

  private readonly scannerVideo = viewChild<ElementRef<HTMLVideoElement>>('scannerVideo');
  private scannerSession?: CameraScannerSession;

  /** Where "back" goes, and which page a scanned code is handed to. */
  private readonly returnTo = this.route.snapshot.queryParamMap.get('returnTo')?.trim() || DEFAULT_RETURN_TO;
  private readonly target = this.route.snapshot.queryParamMap.get('target')?.trim() || DEFAULT_TARGET;

  async ngAfterViewInit(): Promise<void> {
    await this.startScanner();
  }

  ngOnDestroy(): void {
    this.stopScanner();
  }

  protected get canUseScanner(): boolean {
    return this.scanner.isSupported();
  }

  protected async retry(): Promise<void> {
    this.scannerError.set('');
    this.detectedCode.set('');
    this.navigating.set(false);
    await this.startScanner();
  }

  protected close(): void {
    this.stopScanner();
    void this.router.navigateByUrl(this.returnTo);
  }

  protected get statusTitle(): string {
    if (this.navigating()) {
      return 'กำลังเปิดรายการจาก QR';
    }
    return this.scanning() ? 'กำลังสแกน QR' : 'เตรียมเปิดกล้อง';
  }

  protected get statusDescription(): string {
    if (this.navigating()) {
      return 'ตรวจพบข้อมูลแล้ว ระบบกำลังพาไปยังหน้ารับซื้อ/รับสินค้า';
    }
    return this.detectedCode() || 'เมื่อพบรหัสใน QR ระบบจะเปิดหน้าปลายทางให้อัตโนมัติ';
  }

  private async startScanner(): Promise<void> {
    const video = this.scannerVideo()?.nativeElement;
    if (!video) {
      this.scannerError.set('ไม่พบช่องกล้อง');
      return;
    }
    if (!this.scanner.isSupported()) {
      this.scannerError.set('เบราว์เซอร์นี้ยังไม่รองรับกล้องสแกน QR');
      return;
    }

    this.scanning.set(true);
    try {
      this.scannerSession = await this.scanner.start(video, (value) => {
        this.detectedCode.set(value);
        this.scanning.set(false);
        void this.handleScannedValue(value);
      });
    } catch (error) {
      this.scannerError.set(error instanceof Error ? error.message : 'ไม่สามารถเปิดกล้องได้');
      this.scanning.set(false);
    }
  }

  private stopScanner(): void {
    this.scannerSession?.stop();
    this.scannerSession = undefined;
    this.scanning.set(false);
  }

  /**
   * A trade QR carries a bare code (`O-…` sale offer, `T-…` record or `S-…`
   * stock QR); a legacy QR may carry a deep link. Both end up on the receive
   * page, which resolves the code itself.
   */
  private async handleScannedValue(value: string): Promise<void> {
    const raw = value.trim();
    if (!raw) {
      this.scannerError.set('ไม่พบข้อมูลใน QR code');
      return;
    }

    this.stopScanner();
    this.navigating.set(true);

    const code = this.extractCode(raw);
    if (!code) {
      const external = this.externalUrl(raw);
      if (external) {
        window.location.href = external;
        return;
      }
      this.navigating.set(false);
      this.scannerError.set('QR code นี้ไม่รองรับในหน้านี้');
      return;
    }

    // The receive page resolves the code (sale offer, lot or stock) and shows
    // the right message, so the scanner never blocks a valid offer code.
    await this.delay(MobileQrScan.NAVIGATION_DELAY_MS);
    await this.router.navigate([this.target], {queryParams: {code}});
  }

  /** Bare code, or the `code` parameter of a deep link to this app. */
  private extractCode(raw: string): string | null {
    if (this.isBareQrCode(raw)) {
      return raw;
    }
    try {
      const url = new URL(raw, window.location.origin);
      if (url.origin !== window.location.origin) {
        return null;
      }
      return url.searchParams.get('code')?.trim() || null;
    } catch {
      return null;
    }
  }

  private externalUrl(raw: string): string | null {
    try {
      const url = new URL(raw);
      const isExternalHttp = (url.protocol === 'http:' || url.protocol === 'https:')
        && url.origin !== window.location.origin;
      return isExternalHttp ? url.toString() : null;
    } catch {
      return null;
    }
  }

  /** QR payloads that carry a plain code (e.g. "T-<uuid>", "S-<uuid>"). */
  private isBareQrCode(value: string): boolean {
    const candidate = value.trim();
    return candidate.length > 0
      && candidate.length <= 64
      && !candidate.includes('/')
      && !candidate.includes(':')
      && !candidate.includes(' ');
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => {
      window.setTimeout(resolve, ms);
    });
  }
}
